import React from 'react';
import { SenteRegistration, SenteRun } from '../hooks/useSente';

const badge = (cls: string, label: string, pulse = false) => (
  <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 text-xs font-medium rounded-full ${cls}`}>
    {pulse && <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />}
    {label}
  </span>
);

export const RegistrationStatusBadge: React.FC<{ status: SenteRegistration['status'] }> = ({ status }) => {
  switch (status) {
    case 'active':
      return badge('bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', 'Active');
    case 'failed':
      return badge('bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', 'Failed');
    case 'disabled':
      // A revoked connection — the delegation was withdrawn (distinct from an active/pending account).
      return badge('bg-gray-100 text-gray-600 dark:bg-slate-800 dark:text-slate-400', 'Revoked');
    default:
      return badge('bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', 'Pending');
  }
};

// Distinguishes an account Sente created from one the customer connected (owns and delegated).
export const AccountKindBadge: React.FC<{ kind?: SenteRegistration['kind'] }> = ({ kind }) => {
  if (kind !== 'connected') return null; // 'created' is the default/implicit — no badge to keep rows quiet
  return badge('bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400', 'Connected');
};

// `blocked` is used for both real failures-to-clear and intended pauses (submit-confirmation,
// manual take-over). The latter are amber "paused", not red "blocked" — pass the error code so the
// badge can tell them apart.
const PAUSED_CODES = ['SUBMIT_CONFIRMATION_REQUIRED', 'MANUAL_INTERVENTION', 'MFA_REQUIRED', 'TOTP_REQUIRED'];

export const RunStatusBadge: React.FC<{ status: SenteRun['status']; errorCode?: string }> = ({ status, errorCode }) => {
  switch (status) {
    case 'running':
      return badge('bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', 'Running', true);
    case 'awaiting_verification':
      return badge('bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', 'Awaiting verification', true);
    case 'blocked':
      return errorCode === 'MANUAL_INTERVENTION'
        ? badge('bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', 'Paused — you have control', true)
        : errorCode && PAUSED_CODES.includes(errorCode)
          ? badge('bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', 'Needs you', true)
          : badge('bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', 'Blocked — needs you');
    case 'completed':
      return badge('bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', 'Completed');
    case 'failed':
      return badge('bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', 'Failed');
    default:
      return badge('bg-gray-100 text-gray-600 dark:bg-slate-800 dark:text-slate-400', 'Queued');
  }
};
